import { apiClient } from './api';

// ---------------------------------------------------------------------------
// Request types
// ---------------------------------------------------------------------------

export interface CardDonationData {
  donorName: string;
  donorEmail: string;
  donorPhone: string;
  cause: string;
  amount: string;
  currency: 'KES' | 'USD';
  /** Paystack transaction reference returned by onSuccess */
  paystackReference: string;
}

export interface CardDonationResponse {
  api_ref: string;
  receipt_number: string | null;
  payment_method: string;
  amount: number;
  currency: string;
  status: 'completed';
}

export interface DonationFormData {
  donorName: string;
  donorEmail: string;
  donorPhone: string;
  cause: string;
  amount: string;
  currency: 'KES' | 'USD';
  /** 'mpesa' | 'card' */
  paymentMethod: 'mpesa' | 'card';
  /** M-Pesa: the phone number to send the STK push to (may differ from donorPhone) */
  mpesaPhone?: string;
}

// ---------------------------------------------------------------------------
// Response types
// ---------------------------------------------------------------------------

/** Response from POST /donation/initiate */
export interface InitiateDonationResponse {
  api_ref: string;
  paystack_reference: string;
  payment_method: 'M-Pesa' | 'Card';
  status: 'pending';
  /** Card only: Paystack access_code to pass to PaystackPop.resumeTransaction() */
  access_code?: string;
  authorization_url?: string;
  /** M-Pesa only: message to show the user ("Please complete authorization…") */
  display_text?: string;
}

/** Response from GET /donation/status */
export interface DonationStatusResponse {
  status: 'pending' | 'completed' | 'failed';
  receipt_number: string | null;
  payment_method: string | null;
  failed_reason: string | null;
  donation?: Record<string, unknown>;
}

// ---------------------------------------------------------------------------
// Service class
// ---------------------------------------------------------------------------

class DonationService {
  /**
   * Initiate a donation with Paystack payment.
   * Returns pending status + access_code (card) or display_text (M-Pesa).
   */
  async initiateDonation(
    formData: DonationFormData
  ): Promise<InitiateDonationResponse> {
    const response = await apiClient.post<InitiateDonationResponse>(
      'donation/initiate',
      formData
    );

    if (!response.success || !response.data) {
      throw new Error(response.message || 'Failed to initiate donation');
    }

    return response.data;
  }

  /**
   * Poll the status of a pending donation.
   * The backend also calls Paystack's verify endpoint when status is pending,
   * so this handles both webhook-confirmed and localhost-only scenarios.
   */
  async getDonationStatus(apiRef: string): Promise<DonationStatusResponse> {
    const response = await apiClient.get<DonationStatusResponse>(
      'donation/status',
      { api_ref: apiRef }
    );

    if (!response.success || !response.data) {
      throw new Error(response.message || 'Failed to get donation status');
    }

    return response.data;
  }

  /**
   * Verify a completed Paystack card payment and record the donation.
   * Called after PaystackPop.newTransaction() onSuccess fires.
   */
  async confirmCardDonation(data: CardDonationData): Promise<CardDonationResponse> {
    const response = await apiClient.post<CardDonationResponse>(
      'donation/card-complete',
      data
    );
    if (!response.success || !response.data) {
      throw new Error(response.message || 'Failed to confirm card donation');
    }
    return response.data;
  }

  /**
   * Get all donations (admin).
   */
  async getAll(filters?: {
    status?: string;
    cause?: string;
    search?: string;
  }): Promise<any[]> {
    const params =
      filters && Object.keys(filters).length > 0
        ? (filters as Record<string, string>)
        : undefined;

    const response = await apiClient.get<any[]>('donation/list', params);

    if (!response.success || !response.data) {
      throw new Error(response.message || 'Failed to fetch donations');
    }

    return response.data;
  }
}

export const donationService = new DonationService();
